import {
  portableBoardSnapshotV1Schema,
  type PortableBoardSnapshotV1,
} from "./portable-snapshot";
import {
  snapshotManifestV1Schema,
  type SnapshotManifestV1,
} from "./snapshot-manifest";

export type SnapshotAttachmentOutcome =
  SnapshotManifestV1["attachments"][number] & {
    warningMessage: string | null;
  };

function countCheckItems(snapshot: PortableBoardSnapshotV1): number {
  return snapshot.checklists.reduce(
    (total, checklist) => total + checklist.items.length,
    0,
  );
}

function countCustomFieldItems(snapshot: PortableBoardSnapshotV1): number {
  return snapshot.cards.reduce(
    (total, card) => total + card.customFieldItems.length,
    0,
  );
}

export function createSnapshotManifest(
  snapshotInput: PortableBoardSnapshotV1,
  outcomes: readonly SnapshotAttachmentOutcome[],
): SnapshotManifestV1 {
  const snapshot = portableBoardSnapshotV1Schema.parse(snapshotInput);
  const outcomeById = new Map(
    outcomes.map((outcome) => [outcome.attachmentId, outcome]),
  );

  if (
    outcomeById.size !== outcomes.length ||
    outcomeById.size !== snapshot.attachments.length
  ) {
    throw new RangeError(
      "Attachment outcomes must contain every snapshot attachment exactly once.",
    );
  }

  const attachments = snapshot.attachments.map((attachment) => {
    const outcome = outcomeById.get(attachment.id);
    if (
      outcome === undefined ||
      outcome.cardId !== attachment.cardId ||
      outcome.sourceType !== attachment.sourceType
    ) {
      throw new RangeError(
        "Attachment outcomes must match the snapshot attachment identity and source.",
      );
    }

    return {
      archivePath: outcome.archivePath,
      attachmentId: outcome.attachmentId,
      byteSize: outcome.byteSize,
      cardId: outcome.cardId,
      sha256: outcome.sha256,
      sourceType: outcome.sourceType,
      status: outcome.status,
      warningCode: outcome.warningCode,
    };
  });
  const warnings = outcomes.flatMap((outcome) => {
    if (outcome.warningCode === null || outcome.warningMessage === null) {
      return [];
    }

    return [
      {
        code: outcome.warningCode,
        message: outcome.warningMessage,
        objectId: outcome.attachmentId,
        objectType: "attachment" as const,
      },
    ];
  });

  if (
    outcomes.some(
      (outcome) =>
        (outcome.warningCode === null) !== (outcome.warningMessage === null),
    )
  ) {
    throw new RangeError(
      "Attachment warning codes and messages must either both be present or both be absent.",
    );
  }

  return snapshotManifestV1Schema.parse({
    attachments,
    capturedAt: snapshot.capturedAt,
    counts: {
      actions: snapshot.activities.length,
      attachments: snapshot.attachments.length,
      cards: snapshot.cards.length,
      checkItems: countCheckItems(snapshot),
      checklists: snapshot.checklists.length,
      comments: snapshot.comments.length,
      customFieldItems: countCustomFieldItems(snapshot),
      customFields: snapshot.customFields.length,
      labels: snapshot.labels.length,
      lists: snapshot.lists.length,
      members: snapshot.members.length,
    },
    product: snapshot.product,
    provider: snapshot.provider,
    schemaVersion: snapshot.schemaVersion,
    snapshotId: snapshot.snapshotId,
    source: snapshot.source,
    status: warnings.length === 0 ? "complete" : "complete_with_warnings",
    warnings,
  });
}
